import { ListChildComponentProps, VariableSizeList } from "react-window";
import React, { RefObject } from "react";
import { ListSubheader, useMediaQuery, useTheme } from "@mui/material";

const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: (style.top as number) + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});
OuterElementType.displayName = "OuterElementType";

function useResetCache(data: number): RefObject<VariableSizeList> {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

export type VirtualizedListboxComponentProps =
  React.HTMLAttributes<HTMLElement> & {
    itemSize?: number;
  };

// Adapter for react-window
export const VirtualizedListboxComponent = React.forwardRef<
  HTMLDivElement,
  VirtualizedListboxComponentProps
>((props, ref) => {
  const { children, ...other } = props;

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"));

  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;

  const itemSize = props.itemSize ?? smUp ? 36 : 48;

  const getChildSize = (child: unknown) => {
    if (React.isValidElement(child) && child.type === ListSubheader) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

VirtualizedListboxComponent.displayName = "VirtualizedListboxComponent";
